import { buildCacheKey } from "@cfg/kernel";
import type { KernelClient } from "@cfg/kernel/worker";
import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useStore } from "zustand";
import { MeshCache } from "./cache.ts";
import { createConfiguratorStore, type ConfiguratorState, type ConfiguratorStore } from "./store.ts";

export interface ConfiguratorContextValue {
  store: ConfiguratorStore;
  client: KernelClient;
  cache: MeshCache;
}

const Ctx = createContext<ConfiguratorContextValue | null>(null);

export interface ConfiguratorProviderProps {
  client: KernelClient;
  /** `${designId}@${version}` */
  designKey: string;
  /** Bundle URL; omit when the worker already has the design bundled in (design-studio). */
  bundleUrl?: string;
  /** Params to open with (a saved config, URL state); merged over the defaults. */
  initialParams?: Record<string, unknown>;
  /** Fired after every successful preview build with the params it was built from. */
  onParamsChange?: (params: Record<string, unknown>) => void;
  onError?: (err: Error) => void;
  debounceMs?: number;
  cache?: MeshCache;
  children: ReactNode;
}

const sharedCache = new MeshCache();

export function ConfiguratorProvider(props: ConfiguratorProviderProps) {
  const { client, designKey, bundleUrl, initialParams, onParamsChange, onError, debounceMs = 60 } = props;
  const store = useMemo(() => createConfiguratorStore(), []);
  const cache = props.cache ?? sharedCache;
  const onParamsChangeRef = useRef(onParamsChange);
  onParamsChangeRef.current = onParamsChange;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const initialRef = useRef(initialParams);
  initialRef.current = initialParams;

  // Load (or describe) the design whenever the key changes.
  useEffect(() => {
    let cancelled = false;
    store.setState({ status: "loading", error: null, key: designKey, mesh: null, stats: null, issues: [], builtKey: null });
    const load = bundleUrl ? client.load(designKey, bundleUrl) : client.describe(designKey);
    load
      .then((description) => {
        if (cancelled) return;
        const params = { ...description.defaults, ...(initialRef.current ?? {}) };
        store.setState({ description, params, baseline: params, status: "building" });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        store.setState({ status: "error", error: err.message });
        onErrorRef.current?.(err);
      });
    return () => {
      cancelled = true;
    };
  }, [client, designKey, bundleUrl, store]);

  // Rebuild on param changes (debounced, latest wins, cached).
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let lastKey = "";
    const run = async (s: ConfiguratorState) => {
      if (!s.description || !s.key) return;
      const key = buildCacheKey(s.description.id, s.description.version, s.params, "preview");
      if (key === lastKey) return;
      lastKey = key;
      const cached = await cache.get(key);
      if (cached) {
        store.setState({ mesh: cached.mesh, stats: cached.stats, issues: cached.issues, status: "ready", error: null, builtKey: key, buildCount: store.getState().buildCount + 1 });
        onParamsChangeRef.current?.(s.params);
        return;
      }
      store.setState({ status: "building" });
      const result = await client.buildPreview(s.key, s.params);
      if (!result) return; // superseded by a newer request
      if (lastKey !== key) return; // stale
      void cache.put(key, result);
      store.setState({ mesh: result.mesh, stats: result.stats, issues: result.issues, status: "ready", error: null, builtKey: key, buildCount: store.getState().buildCount + 1 });
      onParamsChangeRef.current?.(s.params);
    };
    const unsub = store.subscribe((s, prev) => {
      if (s.params === prev.params && s.description === prev.description) return;
      if (!s.description) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        run(store.getState()).catch((err: Error) => {
          store.setState({ status: "error", error: err.message });
          onErrorRef.current?.(err);
        });
      }, debounceMs);
    });
    client.previewQueue.onError = (err) => {
      store.setState({ status: "error", error: err.message });
      onErrorRef.current?.(err);
    };
    return () => {
      unsub();
      if (timer) clearTimeout(timer);
    };
  }, [client, store, cache, debounceMs]);

  const value = useMemo<ConfiguratorContextValue>(() => ({ store, client, cache }), [store, client, cache]);
  return <Ctx.Provider value={value}>{props.children}</Ctx.Provider>;
}

export function useConfiguratorContext(): ConfiguratorContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useConfigurator* hooks must be used inside <ConfiguratorProvider>");
  return v;
}

/** Like `useConfiguratorContext` but null outside a provider (widgets used standalone). */
export function useOptionalConfiguratorContext(): ConfiguratorContextValue | null {
  return useContext(Ctx);
}

export function useConfiguratorStore<T>(selector: (s: ConfiguratorState) => T): T {
  const { store } = useConfiguratorContext();
  return useStore(store, selector);
}
